import { useState } from "react";
import { ArrowRight, Mail, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function FinalCTA() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", message: "" });
    }, 800);
  };

  return (
    <section
      id="contact"
      data-testid="final-cta-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="relative rounded-3xl overflow-hidden border border-[#FF5722]/20 bg-gradient-to-br from-[#1a0a05] via-[#0a0a0a] to-black p-8 sm:p-12 lg:p-16">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#FF5722]/20 blur-[140px] rounded-full pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-[#FF5722]/10 blur-[120px] rounded-full pointer-events-none" />

          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs font-mono tracking-widest text-[#FF5722] uppercase mb-4">
                // Get Started
              </div>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-white leading-[1.05]">
                Ready to <span className="gradient-text">Scale</span> Your Business?
              </h2>
              <p className="mt-6 text-lg text-zinc-400 max-w-md">
                Let our team build high-performing campaigns that drive real growth.
                Book your free strategy call today.
              </p>
              <div className="mt-8 space-y-3 text-sm text-zinc-400">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#FF5722]" />
                  hello@rhtechnology.com
                </div>
              </div>
            </div>

            <form onSubmit={onSubmit} data-testid="contact-form" className="glass rounded-2xl p-6 sm:p-7 space-y-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5 block">Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-xl focus-visible:ring-[#FF5722]"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@company.com"
                    className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 h-12 rounded-xl focus-visible:ring-[#FF5722]"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5 block">Tell us about your goals</label>
                <Textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="What kind of growth are you targeting?"
                  rows={4}
                  className="bg-black/40 border-white/10 text-white placeholder:text-zinc-600 rounded-xl focus-visible:ring-[#FF5722]"
                />
              </div>

              <Button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF5722] hover:bg-[#FF7043] text-black font-semibold rounded-xl h-12 text-base shadow-[0_0_30px_-8px_rgba(255,87,34,0.6)] group"
              >
                {loading ? "Sending..." : (
                  <>
                    Start Your Campaign
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
